import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, ShieldCheck, Loader2 } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { FormField, Input } from '../../components/ui/FormField'
import { env } from '../../config/env'
import BridgerTextLogo from '../../assets/bridger-primary-logo.svg'
import BridgerIconLogo from '../../assets/bridger-primary-logo-cropped.svg'

/* ─── Zod validation schema ─────────────────────────── */
const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email address'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters'),
})

export default function LoginPage() {
  const { login }       = useAuth()
  const [showPass, setShowPass] = useState(false)
  const [apiError, setApiError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async ({ email, password }) => {
    setApiError('')
    try {
      await login(email, password)
    } catch (err) {
      setApiError(err?.message || 'Login failed. Please try again.')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 p-4">
      <div className="w-full max-w-md animate-fade-in">

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* Top accent bar */}
          <div className="h-1.5 bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700" />

          <div className="p-8">
            {/* Brand */}
            {/* <div className="flex items-center gap-3 mb-8">
              <div className="h-10 w-10 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
                <ShieldCheck className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900 leading-tight">
                  {env.appName}
                </h1>
                <p className="text-xs text-slate-500">Enterprise Audit Platform</p>
              </div>
            </div> */}
                  <div className="flex items-center gap-2 min-w-0 pb-4">
                    <img
                      src={BridgerIconLogo}
                      alt="Bridger Icon"
                      className="h-8 w-8 object-contain shrink-0"
                    />
                    <div className="flex flex-col min-w-0">
                      <img
                        src={BridgerTextLogo}
                        alt="Bridger Insurance"
                        className="h-5 object-contain object-left"
                      />
                      <span className="text-[#00b4d8] text-[10px] font-semibold tracking-widest uppercase leading-none mt-0.5">
                        SmartOps
                      </span>
                    </div>
                  </div>

            <h2 className="text-xl font-semibold text-slate-900 mb-1">Sign in</h2>
            <p className="text-sm text-slate-500 mb-6">
              Enter your credentials to access the platform
            </p>

            {/* API error */}
            {apiError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <span className="font-semibold">Error:</span> {apiError}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
              {/* Email */}
              <FormField label="Email address" required error={errors.email?.message}>
                <Input
                  type="email"
                  placeholder="admin@test.com"
                  autoComplete="email"
                  error={!!errors.email}
                  {...register('email')}
                />
              </FormField>

              {/* Password */}
              <FormField label="Password" required error={errors.password?.message}>
                <div className="relative">
                  <Input
                    type={showPass ? 'text' : 'password'}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    error={!!errors.password}
                    className="pr-10"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPass((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    {showPass
                      ? <EyeOff className="h-4 w-4" />
                      : <Eye    className="h-4 w-4" />}
                  </button>
                </div>
              </FormField>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full justify-center py-2.5 mt-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
          </div>

          {/* Demo credentials hint */}
          {env.isDev && (
            <div className="px-8 pb-6 -mt-2">
              {/* <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-500">
                <span className="font-semibold text-slate-600">Demo: </span>
               user@bridgerins.com / Bridger123
              </div> */}
            </div>
          )}
        </div>

        <p className="text-center text-slate-500 text-xs mt-6">
          © {new Date().getFullYear()} {env.appName}. All rights reserved.
        </p>
      </div>
    </div>
  )
}
